import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../hooks/useAuth';

/** Digits only, 6 to 10: the project's code length is a setting (#1582). */
const CODE_RE = /^[0-9]{6,10}$/;
const RESEND_COOLDOWN_S = 60;

/**
 * Sign-in by emailed code (#1649), for existing accounts only: accounts are
 * made in the tripcode app. "Email me a code" sends one (`shouldCreateUser:
 * false`); the code works in any browser. The email still carries a link
 * until the cut-over, which lands on /auth/callback as before.
 */
export function SignIn() {
  const { session, loading } = useAuthContext();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (loading) return <div className="center-page"><div className="spinner" /></div>;
  if (session) return <Navigate to="/" replace />;

  const callbackUrl = `${window.location.origin}/auth/callback`;
  const trimmedEmail = email.trim().toLowerCase();

  async function sendCode() {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: trimmedEmail,
      options: { emailRedirectTo: callbackUrl, shouldCreateUser: false },
    });
    setSubmitting(false);
    if (err) {
      setError(sendErrorMessage(err));
      return;
    }
    setCode('');
    setStep('code');
    setCooldown(RESEND_COOLDOWN_S);
    setNotice(`We sent a code to ${trimmedEmail}.`);
  }

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    await sendCode();
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const token = code.replace(/[^0-9]/g, '');
    if (!CODE_RE.test(token)) {
      setError('Enter the code from the email.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await supabase.auth.verifyOtp({ email: trimmedEmail, token, type: 'email' });
    setSubmitting(false);
    // Success: useAuth's onAuthStateChange catches it; <Navigate> above
    // redirects on the next render.
    if (err) setError(/expired|invalid/i.test(err.message) ? 'That code was already used or has expired. Send a new one.' : err.message);
  }

  async function handleOAuth(provider: 'google' | 'apple') {
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: callbackUrl },
    });
    if (err) setError(err.message);
  }

  return (
    <div className="center-page">
      <div className="card" style={{ maxWidth: 420, width: '100%' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, letterSpacing: '-0.02em' }}>
          tripcode marketing
        </h1>
        <p style={{ margin: '0 0 20px', color: 'var(--color-text-muted)' }}>
          Admin only. Sign in with the same account you use in the mobile app.
        </p>

        {step === 'code' ? (
          <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {notice && <div className="alert alert-info">{notice}</div>}
            <div className="field">
              <label className="field-label" htmlFor="code">Code</label>
              <input
                id="code"
                className="input"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="Code from the email"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Checking…' : 'Sign in'}
            </button>
            <button type="button" className="btn btn-secondary" disabled={submitting || cooldown > 0} onClick={() => void sendCode()}>
              Send a new code
            </button>
            {cooldown > 0 && (
              <p className="field-hint" style={{ margin: 0, textAlign: 'center' }}>You can ask for another code in {cooldown}s.</p>
            )}
            <button type="button" className="btn btn-ghost" onClick={() => { setStep('email'); setError(null); setNotice(null); }}>
              Use a different email
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="field">
                <label className="field-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  className="input"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={submitting || !trimmedEmail}>
                {submitting ? 'Sending…' : 'Email me a code'}
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
              <span style={{ fontSize: 12, color: 'var(--color-text-subtle)' }}>or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={() => handleOAuth('google')}
              >
                Continue with Google
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={() => handleOAuth('apple')}
              >
                Continue with Apple
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginTop: 16 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
// GoTrue's answers to a send: no account (422 otp_disabled), the per-address
// limit (429), anything else as is.
function sendErrorMessage(err: { message: string; code?: string }): string {
  if (err.code === 'otp_disabled' || /signups not allowed/i.test(err.message)) {
    return "There's no tripcode account for this email yet. Create one in the tripcode app first.";
  }
  if (/only request this after|rate limit|too many/i.test(err.message)) return 'Please wait a minute before asking for another code.';
  return err.message;
}
